import Script from "next/script"

import Storyblok from "@src/utilities/storyblok"

import useStoryblokBridge from "@src/hooks/use-storyblok-bridge"

import getGlobalSiteSettings from "@src/pages/api/get-global-site-settings"

import { getUser } from "@src/redux/user.slice"
import { useAppSelector } from "@src/hooks/use-redux"

import { maybeScrubPropsForCleanSite } from "@src/utilities/clean-site"
import populateCards from "@src/utilities/populate-cards"
import getStoryblokApiKey from "@src/utilities/storyblok-api-key"

import getStoryblokCacheVersion from "@src/workers/storyblok/get-cache-version"

import GeneralLayout from "@src/layouts/general"

import LoadingFullScreen from "@src/components/LoadingFullScreen"
import SEO from "@src/components/seo"

import type { GetStaticPropsContext, InferGetStaticPropsType } from "next"
import type { SiteSettings } from "@src/entities/types/menu"

export default function Page({
  preview,
  siteSettings,
  story,
  storyblokApiKey,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const user = useAppSelector(getUser)
  const blok = useStoryblokBridge(story, storyblokApiKey, preview)

  if (!user.authStateKnown) {
    return <LoadingFullScreen showing={true} text="Loading Rosy Rewards" />
  }

  return (
    <GeneralLayout siteSettings={siteSettings}>
      {blok.content && blok.content.seo && <SEO {...blok.content.seo} />}
      <Script src="https://cdn-loyalty.yotpo.com/loader/DxXFTh058eGqI46ddc4rTg.js" />
      <Script src="https://cdn-widgetsrepository.yotpo.com/v1/loader/DxXFTh058eGqI46ddc4rTg" />
      <div
        className="yotpo-widget-instance"
        data-yotpo-instance-id="249085"
      ></div>
      {!user.isAnonymous && (
        <div
          id="swell-customer-identification"
          data-authenticated="true"
          data-email={user.email}
          data-id={user.uid}
          style={{ display: "none" }}
        />
      )}
    </GeneralLayout>
  )
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const apiKey = getStoryblokApiKey(context.preview)
  const cacheVersion = await getStoryblokCacheVersion()

  const [siteSettings, storyRequest] = await Promise.all([
    getGlobalSiteSettings(context.preview ?? false),
    Storyblok(apiKey).get(`cdn/stories/rosy-rewards`, {
      cv: cacheVersion,
      version: context.preview ? "draft" : "published",
    }),
  ])

  const story = await populateCards(storyRequest.data.story)

  return {
    props: {
      preview: context.preview ?? false,
      siteSettings: maybeScrubPropsForCleanSite<SiteSettings>(siteSettings),
      story: maybeScrubPropsForCleanSite(story),
      ...(context.preview && { storyblokApiKey: apiKey }),
    },
  }
}
